import React, { useState, useEffect, useMemo, useRef } from "react";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { PlayerPhotoUpload } from "../components/PlayerPhotoUpload";
import {
  listPlayerProfiles,
  createPlayerProfile,
  updatePlayerProfile,
  PlayerProfile,
} from "../store/supabasePlayerProfiles";
import {
  listTeams,
  Team,
  SquadFormat,
  ensureSquad,
  getSquadWithPlayers,
  addPlayerToSquad,
  removePlayerFromSquad,
} from "../store/teams";
import TeamLogo from "../components/broadcast/TeamLogo";
import "./CreatePlayerProfile.css";

interface Props {
  onBack: () => void;
  editPlayer?: PlayerProfile | null;
}

type BattingStyle = "RHB" | "LHB";
type PlayerRole = "Batsman" | "Bowler" | "All-rounder" | "Keeper";

const PLAYING_FORMATS: { key: SquadFormat; label: string }[] = [
  { key: "indoor", label: "Indoor" },
  { key: "t20", label: "T20" },
  { key: "odi", label: "ODI" },
];

const BATTING_STYLES: { value: BattingStyle; label: string }[] = [
  { value: "RHB", label: "Right-Hand Bat (RHB)" },
  { value: "LHB", label: "Left-Hand Bat (LHB)" },
];

const BOWLING_STYLES: { value: string; label: string; abbr: string }[] = [
  { value: "Right-arm fast", label: "Right-arm fast", abbr: "RAF" },
  { value: "Right-arm fast-medium", label: "Right-arm fast-medium", abbr: "RAFM" },
  { value: "Right-arm medium", label: "Right-arm medium", abbr: "RAM" },
  { value: "Left-arm fast", label: "Left-arm fast", abbr: "LAF" },
  { value: "Left-arm fast-medium", label: "Left-arm fast-medium", abbr: "LAFM" },
  { value: "Left-arm medium", label: "Left-arm medium", abbr: "LAM" },
  { value: "Right-arm off-spin", label: "Right-arm off-spin", abbr: "RAOS" },
  { value: "Right-arm leg-spin", label: "Right-arm leg-spin", abbr: "RALS" },
  { value: "Left-arm orthodox spin", label: "Left-arm orthodox spin", abbr: "LAO" },
  { value: "Left-arm wrist spin", label: "Left-arm wrist spin", abbr: "LAWS" },
  { value: "Does not bowl", label: "Does not bowl", abbr: "—" },
];

const ROLES: { value: PlayerRole; label: string }[] = [
  { value: "Batsman", label: "Batsman" },
  { value: "Bowler", label: "Bowler" },
  { value: "All-rounder", label: "All-rounder" },
  { value: "Keeper", label: "Wicketkeeper" },
];

const STEPS = ["Basic Details", "Cricket Details", "Teams", "Review"] as const;

const CreatePlayerProfile: React.FC<Props> = ({ onBack, editPlayer }) => {
  const isEditMode = !!editPlayer;
  const [step, setStep] = useState(0);
  const [fullName, setFullName] = useState("");
  const [scorecardName, setScorecardName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [formats, setFormats] = useState<SquadFormat[]>([]);
  const [battingStyle, setBattingStyle] = useState<BattingStyle>("RHB");
  const [bowlingStyle, setBowlingStyle] = useState<string>("");
  const [role, setRole] = useState<PlayerRole>("Batsman");
  const [selectedTeams, setSelectedTeams] = useState<Team[]>([]);
  const [originalTeamIds, setOriginalTeamIds] = useState<Set<string>>(new Set());
  const [teamSearch, setTeamSearch] = useState("");
  const [allTeams, setAllTeams] = useState<Team[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [existingPlayers, setExistingPlayers] = useState<PlayerProfile[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadExistingPlayers();
    if (editPlayer) {
      prefillFromProfile(editPlayer);
      loadTeamsAndPlayerSquads(editPlayer.id);
    } else {
      loadTeams();
    }
  }, []);

  async function loadTeams() {
    setTeamsLoading(true);
    try {
      const [indoor, t20, odi] = await Promise.all([
        listTeams("indoor"),
        listTeams("t20"),
        listTeams("odi"),
      ]);
      const merged = [...indoor, ...t20, ...odi];
      const deduped = merged.filter(
        (t, idx, arr) => arr.findIndex((x) => x.id === t.id) === idx
      );
      setAllTeams(deduped);
    } catch (err) {
      console.error("[CreatePlayerProfile] Error loading teams:", err);
    } finally {
      setTeamsLoading(false);
    }
  }

  async function loadExistingPlayers() {
    try {
      const players = await listPlayerProfiles();
      setExistingPlayers(players);
    } catch (err) {
      console.error("[CreatePlayerProfile] Error loading existing players:", err);
    }
  }

  function prefillFromProfile(profile: PlayerProfile) {
    setFullName(profile.name || "");
    setScorecardName(profile.nickname || "");
    setPhotoUrl(profile.photo_url || "");
    setBattingStyle((profile.batting_style as BattingStyle) || "RHB");
    setBowlingStyle(profile.bowling_style || "");
    setRole((profile.role as PlayerRole) || "Batsman");
    setFormats(
      Array.isArray(profile.formats)
        ? (profile.formats.filter((f) =>
            ["indoor", "t20", "odi"].includes(f)
          ) as SquadFormat[])
        : []
    );
  }

  async function loadTeamsAndPlayerSquads(playerId: string) {
    setTeamsLoading(true);
    try {
      const [indoor, t20, odi] = await Promise.all([
        listTeams("indoor"),
        listTeams("t20"),
        listTeams("odi"),
      ]);
      const merged = [...indoor, ...t20, ...odi];
      const deduped = merged.filter(
        (t, idx, arr) => arr.findIndex((x) => x.id === t.id) === idx
      );
      setAllTeams(deduped);

      // Load squad memberships. If a squad fetch fails for a particular team,
      // skip that team rather than wiping all memberships.
      const playerTeams: Team[] = [];
      for (const team of deduped) {
        try {
          const squad = await getSquadWithPlayers(team.id, team.format);
          if (squad?.players.some((p) => p.id === playerId)) {
            playerTeams.push(team);
          }
        } catch (err) {
          console.warn(
            `[CreatePlayerProfile] Could not load squad for team ${team.id}, skipping:`,
            err
          );
        }
      }

      setSelectedTeams(playerTeams);
      setOriginalTeamIds(new Set(playerTeams.map((t) => t.id)));

      // Formats are loaded from the profile in prefillFromProfile and are
      // independent of team memberships. Do not overwrite them here.
    } catch (err) {
      console.error("[CreatePlayerProfile] Error loading player teams:", err);
    } finally {
      setTeamsLoading(false);
    }
  }

  const duplicateMatches = useMemo(() => {
    if (!fullName.trim()) return [];
    const q = fullName.trim().toLowerCase();
    return existingPlayers.filter(
      (p) =>
        (isEditMode ? p.id !== editPlayer?.id : true) &&
        (p.name.toLowerCase().includes(q) ||
          q.includes(p.name.toLowerCase()))
    );
  }, [fullName, existingPlayers, isEditMode, editPlayer]);

  const filteredTeams = useMemo(() => {
    const q = teamSearch.trim().toLowerCase();
    if (!q) return [];
    return allTeams
      .filter((t) => t.name.toLowerCase().includes(q))
      .filter((t) => !selectedTeams.some((s) => s.id === t.id))
      .slice(0, 10);
  }, [teamSearch, allTeams, selectedTeams]);

  function toggleFormat(format: SquadFormat) {
    setFormats((prev) =>
      prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]
    );
  }

  function addTeam(team: Team) {
    if (selectedTeams.some((t) => t.id === team.id)) return;
    setSelectedTeams((prev) => [...prev, team]);
    setTeamSearch("");
  }

  function removeTeam(teamId: string) {
    setSelectedTeams((prev) => prev.filter((t) => t.id !== teamId));
  }

  function canProceed(): boolean {
    if (step === 0) return fullName.trim().length > 0;
    if (step === 1) return formats.length > 0 && battingStyle.length > 0;
    return true;
  }

  function handleNext() {
    if (!canProceed()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleBack() {
    if (step === 0) {
      onBack();
      return;
    }
    setStep((s) => Math.max(s - 1, 0));
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleCancel() {
    onBack();
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const profileData = {
        name: fullName.trim(),
        nickname: scorecardName.trim() || null,
        role,
        batting_style: battingStyle,
        bowling_style: bowlingStyle && bowlingStyle !== "Does not bowl"
          ? bowlingStyle.trim()
          : undefined,
        photo_url: photoUrl.trim() || undefined,
        is_guest: false,
        formats,
      };

      let savedProfile: PlayerProfile | null;

      if (isEditMode && editPlayer) {
        savedProfile = await updatePlayerProfile(editPlayer.id, profileData);
      } else {
        savedProfile = await createPlayerProfile(profileData);
      }

      if (!savedProfile) {
        throw new Error("Saving completed but no profile was returned by the database.");
      }

      // Sync team squad memberships. We diff the selected teams against the
      // original set (for edit mode) and only add/remove what changed.
      const selectedIds = new Set(selectedTeams.map((t) => t.id));
      const toAdd = selectedTeams.filter((t) => !originalTeamIds.has(t.id));
      const toRemove = isEditMode
        ? Array.from(originalTeamIds).filter((id) => !selectedIds.has(id))
        : [];

      // Look up squad IDs for teams being removed, then remove the player.
      for (const teamId of toRemove) {
        const team = allTeams.find((t) => t.id === teamId);
        if (!team) continue;
        try {
          const squad = await getSquadWithPlayers(team.id, team.format);
          if (squad?.squad) {
            await removePlayerFromSquad(squad.squad.id, savedProfile!.id);
          }
        } catch (err) {
          console.warn(
            `[CreatePlayerProfile] Could not remove player from squad ${teamId}:`,
            err
          );
        }
      }

      // Ensure a squad exists for each newly-added team, then add the player.
      for (const team of toAdd) {
        try {
          const squad = await ensureSquad(team.id, team.format);
          await addPlayerToSquad(squad.id, savedProfile!.id);
        } catch (err) {
          console.warn(
            `[CreatePlayerProfile] Could not add player to squad ${team.id}:`,
            err
          );
        }
      }

      setSaveSuccess(true);
      // Brief success indication before returning to the player list.
      setTimeout(() => {
        onBack();
      }, 900);
    } catch (err: any) {
      console.error("[CreatePlayerProfile] Save failed:", err);
      setSaveError(err?.message || "Failed to save player profile. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ww-cpw">
      <div className="ww-cpw__scroll" ref={scrollRef}>
        <div className="ww-cpw__inner">
          {/* Header */}
          <header className="ww-cpw__header">
            <div className="ww-cpw__header-row">
              <button
                className="ww-cpw__back"
                onClick={handleBack}
                aria-label="Back to Players"
              >
                <span className="ww-cpw__back-arrow">&#8592;</span>
                <span>Players</span>
              </button>
              <h1 className="ww-cpw__title">{isEditMode ? "Edit Player Profile" : "Create Player Profile"}</h1>
            </div>
            <p className="ww-cpw__intro">
              {isEditMode
                ? "Update this player's details below. Their existing matches, statistics and career records are preserved."
                : "A player can participate in multiple teams and cricket formats. Set up their details once and assign them across your squads."}
            </p>

            {/* Progress indicator */}
            <div className="ww-cpw__progress">
              {STEPS.map((label, i) => (
                <React.Fragment key={label}>
                  <div
                    className={`ww-cpw__step ${i === step ? "is-current" : ""} ${
                      i < step ? "is-done" : ""
                    }`}
                  >
                    <span className="ww-cpw__step-circle">
                      {i < step ? "\u2713" : i + 1}
                    </span>
                    <span className="ww-cpw__step-label">{label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <span
                      className={`ww-cpw__step-line ${i < step ? "is-done" : ""}`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
          </header>

          {/* Step content */}
          <div className="ww-cpw__body">
            {step === 0 && (
              <StepBasicDetails
                fullName={fullName}
                setFullName={setFullName}
                scorecardName={scorecardName}
                setScorecardName={setScorecardName}
                dateOfBirth={dateOfBirth}
                setDateOfBirth={setDateOfBirth}
                photoUrl={photoUrl}
                setPhotoUrl={setPhotoUrl}
              />
            )}

            {step === 1 && (
              <StepCricketDetails
                formats={formats}
                toggleFormat={toggleFormat}
                battingStyle={battingStyle}
                setBattingStyle={setBattingStyle}
                bowlingStyle={bowlingStyle}
                setBowlingStyle={setBowlingStyle}
                role={role}
                setRole={setRole}
              />
            )}

            {step === 2 && (
              <StepTeams
                teamSearch={teamSearch}
                setTeamSearch={setTeamSearch}
                filteredTeams={filteredTeams}
                selectedTeams={selectedTeams}
                addTeam={addTeam}
                removeTeam={removeTeam}
                teamsLoading={teamsLoading}
              />
            )}

            {step === 3 && (
              <StepReview
                isEditMode={isEditMode}
                fullName={fullName}
                scorecardName={scorecardName}
                dateOfBirth={dateOfBirth}
                photoUrl={photoUrl}
                formats={formats}
                battingStyle={battingStyle}
                bowlingStyle={bowlingStyle}
                role={role}
                selectedTeams={selectedTeams}
                duplicateMatches={duplicateMatches}
                saving={saving}
                saveError={saveError}
                saveSuccess={saveSuccess}
                onEdit={(target) => setStep(target)}
                onSave={handleSave}
                onCancel={handleCancel}
              />
            )}
          </div>
        </div>
      </div>

      {/* Fixed nav buttons */}
      {step < 3 && (
        <div className="ww-cpw__nav">
          <button className="ww-cpw__nav-btn ww-cpw__nav-btn--secondary" onClick={handleBack}>
            {step === 0 ? "Cancel" : "Back"}
          </button>
          <button
            className="ww-cpw__nav-btn ww-cpw__nav-btn--primary"
            onClick={handleNext}
            disabled={!canProceed()}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

/* ---------- Step 1: Basic Details ---------- */

interface StepBasicDetailsProps {
  fullName: string;
  setFullName: (v: string) => void;
  scorecardName: string;
  setScorecardName: (v: string) => void;
  dateOfBirth: string;
  setDateOfBirth: (v: string) => void;
  photoUrl: string;
  setPhotoUrl: (v: string) => void;
}

const StepBasicDetails: React.FC<StepBasicDetailsProps> = ({
  fullName,
  setFullName,
  scorecardName,
  setScorecardName,
  dateOfBirth,
  setDateOfBirth,
  photoUrl,
  setPhotoUrl,
}) => {
  return (
    <div className="ww-cpw__card">
      <h2 className="ww-cpw__card-title">Basic Details</h2>
      <p className="ww-cpw__card-desc">
        Tell us who this player is. The scorecard name is what appears during matches.
      </p>

      <div className="ww-cpw__field">
        <label className="ww-cpw__label">
          Full Name <span className="ww-cpw__required">*</span>
        </label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="e.g., James Anderson"
          className="ww-cpw__input"
        />
      </div>

      <div className="ww-cpw__field">
        <label className="ww-cpw__label">Preferred Scorecard Name</label>
        <input
          type="text"
          value={scorecardName}
          onChange={(e) => setScorecardName(e.target.value)}
          placeholder="e.g., J. Anderson"
          className="ww-cpw__input"
        />
        <p className="ww-cpw__hint">
          Optional. If left blank, the player's full name is used on scorecards.
        </p>
      </div>

      <div className="ww-cpw__field">
        <label className="ww-cpw__label">Date of Birth</label>
        <input
          type="date"
          value={dateOfBirth}
          onChange={(e) => setDateOfBirth(e.target.value)}
          className="ww-cpw__input"
        />
        <p className="ww-cpw__hint">
          Date of birth is collected for your reference but is not stored in the
          current database schema.
        </p>
      </div>

      <div className="ww-cpw__field">
        <label className="ww-cpw__label">Player Photo</label>
        <PlayerPhotoUpload
          currentPhotoUrl={photoUrl || undefined}
          playerName={fullName || "New Player"}
          onPhotoUploaded={(url) => setPhotoUrl(url)}
          onPhotoRemoved={() => setPhotoUrl("")}
        />
      </div>
    </div>
  );
};

/* ---------- Step 2: Cricket Details ---------- */

interface StepCricketDetailsProps {
  formats: SquadFormat[];
  toggleFormat: (f: SquadFormat) => void;
  battingStyle: BattingStyle;
  setBattingStyle: (v: BattingStyle) => void;
  bowlingStyle: string;
  setBowlingStyle: (v: string) => void;
  role: PlayerRole;
  setRole: (v: PlayerRole) => void;
}

const StepCricketDetails: React.FC<StepCricketDetailsProps> = ({
  formats,
  toggleFormat,
  battingStyle,
  setBattingStyle,
  bowlingStyle,
  setBowlingStyle,
  role,
  setRole,
}) => {
  return (
    <div className="ww-cpw__card">
      <h2 className="ww-cpw__card-title">Cricket Details</h2>
      <p className="ww-cpw__card-desc">
        These describe the player as an individual, not their team assignment.
      </p>

      {/* Playing Formats */}
      <div className="ww-cpw__field">
        <label className="ww-cpw__label">
          Playing Formats <span className="ww-cpw__required">*</span>
        </label>
        <p className="ww-cpw__hint">Select all formats this player participates in.</p>
        <div className="ww-cpw__chips">
          {PLAYING_FORMATS.map((f) => (
            <button
              key={f.key}
              className={`ww-cpw__chip ${formats.includes(f.key) ? "is-selected" : ""}`}
              onClick={() => toggleFormat(f.key)}
              type="button"
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Role */}
      <div className="ww-cpw__field">
        <label className="ww-cpw__label">Cricket Role</label>
        <p className="ww-cpw__hint">Select the role that best describes this player.</p>
        <div className="ww-cpw__chips">
          {ROLES.map((r) => (
            <button
              key={r.value}
              className={`ww-cpw__chip ${role === r.value ? "is-selected" : ""}`}
              onClick={() => setRole(r.value)}
              type="button"
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Batting Style */}
      <div className="ww-cpw__field">
        <label className="ww-cpw__label">
          Batting Style <span className="ww-cpw__required">*</span>
        </label>
        <div className="ww-cpw__chips">
          {BATTING_STYLES.map((b) => (
            <button
              key={b.value}
              className={`ww-cpw__chip ${battingStyle === b.value ? "is-selected" : ""}`}
              onClick={() => setBattingStyle(b.value)}
              type="button"
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bowling Style */}
      <div className="ww-cpw__field">
        <label className="ww-cpw__label">Bowling Style</label>
        <p className="ww-cpw__hint">Select the bowling style that best describes this player.</p>
        <div className="ww-cpw__select-grid">
          {BOWLING_STYLES.map((b) => (
            <button
              key={b.value}
              className={`ww-cpw__select-item ${bowlingStyle === b.value ? "is-selected" : ""}`}
              onClick={() => setBowlingStyle(b.value)}
              type="button"
            >
              <span className="ww-cpw__select-label">{b.label}</span>
              {b.abbr !== "—" && (
                <span className="ww-cpw__select-abbr">{b.abbr}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------- Step 3: Teams ---------- */

interface StepTeamsProps {
  teamSearch: string;
  setTeamSearch: (v: string) => void;
  filteredTeams: Team[];
  selectedTeams: Team[];
  addTeam: (t: Team) => void;
  removeTeam: (id: string) => void;
  teamsLoading: boolean;
}

const StepTeams: React.FC<StepTeamsProps> = ({
  teamSearch,
  setTeamSearch,
  filteredTeams,
  selectedTeams,
  addTeam,
  removeTeam,
  teamsLoading,
}) => {
  return (
    <div className="ww-cpw__card">
      <h2 className="ww-cpw__card-title">Team Assignments</h2>
      <p className="ww-cpw__card-desc">
        Search and select existing teams from your workspace. A player can belong to
        multiple teams across different formats. Team assignment is optional — you can
        save a player with no teams selected.
      </p>

      {/* Search */}
      <div className="ww-cpw__field">
        <label className="ww-cpw__label">Search existing teams</label>
        <input
          type="text"
          value={teamSearch}
          onChange={(e) => setTeamSearch(e.target.value)}
          placeholder="Type a team name..."
          className="ww-cpw__input"
        />
      </div>

      {/* Search results */}
      {teamSearch.trim() && (
        <div className="ww-cpw__search-results">
          {teamsLoading ? (
            <div className="ww-cpw__search-empty">Loading teams...</div>
          ) : filteredTeams.length === 0 ? (
            <div className="ww-cpw__search-empty">No matching teams found</div>
          ) : (
            filteredTeams.map((team) => (
              <button
                key={team.id}
                className="ww-cpw__search-item"
                onClick={() => addTeam(team)}
                type="button"
              >
                <TeamLogo logoUrl={team.logo_url} teamName={team.name} size="sm" />
                <div className="ww-cpw__search-item-body">
                  <span className="ww-cpw__search-item-name">{team.name}</span>
                  <span className="ww-cpw__search-item-format">
                    {team.format.toUpperCase()}
                  </span>
                </div>
                <span className="ww-cpw__search-item-add">+</span>
              </button>
            ))
          )}
        </div>
      )}

      {/* Selected teams chips */}
      <div className="ww-cpw__field">
        <label className="ww-cpw__label">Selected Teams</label>
        {selectedTeams.length === 0 ? (
          <p className="ww-cpw__hint">No teams selected yet. Search above to add teams.</p>
        ) : (
          <div className="ww-cpw__selected-teams">
            {selectedTeams.map((team) => (
              <div key={team.id} className="ww-cpw__team-chip">
                <div className="ww-cpw__team-chip-logo">
                  {team.logo_url ? (
                    <img
                      src={team.logo_url}
                      alt={team.name}
                      className="ww-cpw__team-chip-img"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <span className="ww-cpw__team-chip-fallback">
                      {getTeamInitials(team.name)}
                    </span>
                  )}
                </div>
                <span className="ww-cpw__team-chip-name">{team.name}</span>
                <span className="ww-cpw__team-chip-format">{team.format.toUpperCase()}</span>
                <button
                  className="ww-cpw__team-chip-remove"
                  onClick={() => removeTeam(team.id)}
                  aria-label={`Remove ${team.name}`}
                  type="button"
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ---------- Step 4: Review ---------- */

interface StepReviewProps {
  isEditMode: boolean;
  fullName: string;
  scorecardName: string;
  dateOfBirth: string;
  photoUrl: string;
  formats: SquadFormat[];
  battingStyle: BattingStyle;
  bowlingStyle: string;
  role: PlayerRole;
  selectedTeams: Team[];
  duplicateMatches: PlayerProfile[];
  saving: boolean;
  saveError: string | null;
  saveSuccess: boolean;
  onEdit: (step: number) => void;
  onSave: () => void;
  onCancel: () => void;
}

const StepReview: React.FC<StepReviewProps> = ({
  isEditMode,
  fullName,
  scorecardName,
  dateOfBirth,
  photoUrl,
  formats,
  battingStyle,
  bowlingStyle,
  role,
  selectedTeams,
  duplicateMatches,
  saving,
  saveError,
  saveSuccess,
  onEdit,
  onSave,
  onCancel,
}) => {
  return (
    <div className="ww-cpw__review">
      <div className="ww-cpw__card">
        <h2 className="ww-cpw__card-title">Review</h2>
        <p className="ww-cpw__card-desc">
          Check the details below. Tap any section label to go back and edit.
        </p>

        {/* Identity */}
        <div className="ww-cpw__review-section">
          <div className="ww-cpw__review-section-header">
            <h3>Player Identity</h3>
            <button className="ww-cpw__review-edit" onClick={() => onEdit(0)} type="button">
              Edit
            </button>
          </div>
          <div className="ww-cpw__review-identity">
            <PlayerAvatar name={fullName || "New Player"} photoUrl={photoUrl || undefined} size="lg" />
            <div className="ww-cpw__review-identity-body">
              <div className="ww-cpw__review-row">
                <span className="ww-cpw__review-key">Full Name</span>
                <span className="ww-cpw__review-val">{fullName || "—"}</span>
              </div>
              <div className="ww-cpw__review-row">
                <span className="ww-cpw__review-key">Scorecard Name</span>
                <span className="ww-cpw__review-val">{scorecardName.trim() || fullName || "—"}</span>
              </div>
              {dateOfBirth && (
                <div className="ww-cpw__review-row">
                  <span className="ww-cpw__review-key">Date of Birth</span>
                  <span className="ww-cpw__review-val">{dateOfBirth} (not stored)</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Cricket Details */}
        <div className="ww-cpw__review-section">
          <div className="ww-cpw__review-section-header">
            <h3>Cricket Details</h3>
            <button className="ww-cpw__review-edit" onClick={() => onEdit(1)} type="button">
              Edit
            </button>
          </div>
          <div className="ww-cpw__review-row">
            <span className="ww-cpw__review-key">Playing Formats</span>
            <span className="ww-cpw__review-val">
              {formats.length > 0
                ? formats.map((f) => f.toUpperCase()).join(", ")
                : "—"}
            </span>
          </div>
          <div className="ww-cpw__review-row">
            <span className="ww-cpw__review-key">Cricket Role</span>
            <span className="ww-cpw__review-val">
              {ROLES.find((r) => r.value === role)?.label || role}
            </span>
          </div>
          <div className="ww-cpw__review-row">
            <span className="ww-cpw__review-key">Batting Style</span>
            <span className="ww-cpw__review-val">
              {battingStyle === "RHB" ? "Right-Hand Bat (RHB)" : "Left-Hand Bat (LHB)"}
            </span>
          </div>
          <div className="ww-cpw__review-row">
            <span className="ww-cpw__review-key">Bowling Style</span>
            <span className="ww-cpw__review-val">{bowlingStyle || "Not set"}</span>
          </div>
        </div>

        {/* Teams */}
        <div className="ww-cpw__review-section">
          <div className="ww-cpw__review-section-header">
            <h3>Team Assignments</h3>
            <button className="ww-cpw__review-edit" onClick={() => onEdit(2)} type="button">
              Edit
            </button>
          </div>
          {selectedTeams.length === 0 ? (
            <p className="ww-cpw__hint">No teams assigned (optional).</p>
          ) : (
            <div className="ww-cpw__selected-teams">
              {selectedTeams.map((team) => (
                <div key={team.id} className="ww-cpw__team-chip">
                  <div className="ww-cpw__team-chip-logo">
                    {team.logo_url ? (
                      <img
                        src={team.logo_url}
                        alt={team.name}
                        className="ww-cpw__team-chip-img"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = "none";
                        }}
                      />
                    ) : (
                      <span className="ww-cpw__team-chip-fallback">
                        {getTeamInitials(team.name)}
                      </span>
                    )}
                  </div>
                  <span className="ww-cpw__team-chip-name">{team.name}</span>
                  <span className="ww-cpw__team-chip-format">
                    {team.format.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Duplicate warning */}
        {duplicateMatches.length > 0 && (
          <div className="ww-cpw__duplicate-warning">
            <h4>Possible Duplicate Players</h4>
            <p>
              Existing profiles with similar names were found. Matching names do not
              always represent the same person. Please verify before continuing.
            </p>
            <div className="ww-cpw__duplicate-list">
              {duplicateMatches.slice(0, 5).map((p) => (
                <div key={p.id} className="ww-cpw__duplicate-item">
                  <PlayerAvatar name={p.name} photoUrl={p.photo_url} size="sm" />
                  <div>
                    <span className="ww-cpw__duplicate-name">{p.name}</span>
                    {p.nickname && (
                      <span className="ww-cpw__duplicate-nick">"{p.nickname}"</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save error */}
      {saveError && (
        <div className="ww-cpw__save-warning">
          <h4>Save Failed</h4>
          <p>{saveError}</p>
        </div>
      )}

      {/* Save success */}
      {saveSuccess && (
        <div className="ww-cpw__save-success">
          <h4>Player Saved</h4>
          <p>Returning to the player list...</p>
        </div>
      )}

      {/* Actions */}
      <div className="ww-cpw__review-actions">
        <button
          className="ww-cpw__nav-btn ww-cpw__nav-btn--secondary"
          onClick={onCancel}
          disabled={saving}
          type="button"
        >
          Cancel
        </button>
        <button
          className="ww-cpw__nav-btn ww-cpw__nav-btn--primary"
          onClick={onSave}
          disabled={saving || saveSuccess}
          type="button"
        >
          {saving
            ? "Saving..."
            : isEditMode
              ? "Save Changes"
              : "Create Player Profile"}
        </button>
      </div>
    </div>
  );
};

/* ---------- Helpers ---------- */

function getTeamInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

export default CreatePlayerProfile;
